<script setup lang="ts">
import { ElNotification } from 'element-plus'
import { confirmedEmail } from '@/components/goat/page/home/RegisterBox'
import { useAuthStore } from '@/stores/auth'
import { ServiceNames, useGoatStore } from '@/stores/goat'
import { ModeEnum } from '@/types/enums'

const { t } = useI18n()
const { updateMode } = siteUtils
const loading = ref(false)
const ban = ref(false)
const auth = useAuthStore()
function capitalizeFirst(str: string) {
  return (str || '').charAt(0).toUpperCase() + (str || '').slice(1)
}
function handleLoginResponse(status: string, token: string, email: string, serviceName = '') {
  const { contextGaRef, gaSections } = gaUtils
  contextGaRef.value = { gs_section: gaSections.loginForm }
  switch (status) {
    case 'ok':
    { auth.login(token, email)
      const { gaLogin, gaLoginMethods, gaSections } = gaUtils
      gaLogin({ method: `${gaLoginMethods.goat}${serviceName?.length ? ',' : ''}${serviceName}`, gs_section: gaSections.loginForm })
      break }
    case 'confirm_email':
      ElNotification({
        title: t('login.notify.emailNotVerified.title'),
        message: t('login.notify.emailNotVerified.message'),
        type: 'warning',
        duration: 0,
        offset: 100,
        appendTo: '#auth-wrapper',
        customClass: 'right-1/2 translate-x-1/2 large absolute'
      })
      confirmedEmail.value = email
      updateMode(ModeEnum.confirmEmail)
      break
    case 'invalid_email_password':
      ElNotification({
        title: t('login.notify.invalidCredentials.title'),
        type: 'error',
        offset: 100,
        duration: 9000,
        appendTo: '#auth-wrapper',
        customClass: 'right-1/2 translate-x-1/2 large absolute'
      })
      break
    case 'to_many_tries':
      ban.value = true
      ElNotification({
        title: t('login.notify.tooManyTries.title'),
        message: t('login.notify.tooManyTries.message'),
        type: 'error',
        duration: 10000,
        offset: 100,
        showClose: false,
        appendTo: '#auth-wrapper',
        customClass: 'right-1/2 translate-x-1/2 large absolute',
        onClose: () => ban.value = false
      })
      break
    case 'accept_terms':
      if (token) {
        const goat = useGoatStore()
        goat.updateGoatDetails({ serviceToken: token, serviceName })
        updateMode(ModeEnum.acceptTerms)
      }
      updateMode(ModeEnum.acceptTerms)
      break
    case 'missing_email':
      ElNotification({
        title: t('login.notify.missingEmail.title', { service: capitalizeFirst(serviceName) }),
        type: 'error',
        offset: 100,
        duration: 9000,
        appendTo: '#auth-wrapper',
        customClass: 'right-1/2 translate-x-1/2 large absolute max-w-full'
      })
      break
    default:
      ElNotification({
        title: t('login.notify.unavailable.title'),
        message: t('login.notify.unavailable.message'),
        type: 'error',
        duration: 9000,
        offset: 100,
        appendTo: '#auth-wrapper',
        customClass: 'right-1/2 translate-x-1/2 large absolute'
      })
  }
}
function onSubmit(data: FormTypes.SubmitForm) {
  loading.value = true
  const { getGoatApiUrl } = siteUtils
  const { getAxiosInstance, catchFormErrors } = appUtilsPlugin
  ElNotification.closeAll()
  getAxiosInstance()?.post(getGoatApiUrl('login'), { ...data.form }).then((response) => {
    handleLoginResponse(response?.data?.status || '', response?.data?.token ?? '', response?.data?.email ?? (data.form?.email || ''))
  }).catch(catchFormErrors(data.externalErrors, { appendTo: '#auth-wrapper' })).then(() => {
    loading.value = false
  })
}

function handleGoogleCredentialResponse(token: string) {
  const { getGoatApiUrl } = siteUtils
  const { getAxiosInstance, catchError } = appUtilsPlugin
  ElNotification.closeAll()
  getAxiosInstance()
    ?.post(getGoatApiUrl('gmail-login'), { token })
    .then((response) => {
      handleLoginResponse(response?.data?.status || '', response?.data?.token ?? token, response?.data?.email ?? '', ServiceNames.gmail)
    })
    .catch(catchError({ appendTo: '#auth-wrapper' }))
}
function handleFbCredentialResponse(token: string) {
  const { getGoatApiUrl } = siteUtils
  const { getAxiosInstance, catchError } = appUtilsPlugin
  ElNotification.closeAll()
  getAxiosInstance()
    ?.post(getGoatApiUrl('facebook-login'), { token })
    .then((response) => {
      handleLoginResponse(response?.data?.status || '', response?.data?.token ?? token, response?.data?.email ?? '', ServiceNames.facebook)
    })
    .catch(catchError({ appendTo: '#auth-wrapper' }))
}
// const isEnabledFb = import.meta.env.VITE_PROD !== 'on' || !!localStorage?.getItem('isEnabledFb')
const isEnabledFb = true
</script>

<template>
  <div class="box-form-wrapper mx-auto max-w-2xl big:max-w-(--breakpoint-xl) big:px-16 mt-5">
    <div
      class="box-form big:mt-10 mx-3 bg-white pt-10 big:pt-16 pb-10 big:pb-16 px-2 big:px-16 rounded-2xl flex flex-col items-center"
    >
      <div class="mx-auto w-full max-w-md">
        <h1 class="text-lg big:text-2xl uppercase font-semibold text-center tracking-wider">
          {{ t('login.title') }}
        </h1>
        <goat-form-component
          class="mt-6 big:mb-5 w-full"
          size="large"
          hide-required-asterisk
          @submit="onSubmit"
        >
          <template #default="{ submit, form }">
            <goat-form-field-text-trim
              v-focus
              api-field-name="email"
              :placeholder="t('form.email')"
              :label="!!form?.email?.length ? t('form.email') : false"
              class="mt-1 big:mt-1.5 text-lg"
              required
              :rules="[{ type: 'email', trigger: 'blur', message: t('form.validation.notValidEmail') }]"
              @keyup.enter.stop.prevent="submit"
            />
            <el-row
              class="px-0 w-full relative"
            >
              <goat-form-field-text-trim
                api-field-name="password"
                :placeholder="t('form.password')"
                :label="!!form?.password?.length ? t('form.password') : false"
                class="mt-1 big:mt-1.5 text-lg"
                type="password"
                show-password
                :rules="[{ type: 'string', trigger: 'blur', min: 6, message: t('form.validation.atLeast6Chars') }]"
                required
                @keyup.enter.stop.prevent="submit"
              />
              <el-button
                type="primary"
                link
                class="absolute -bottom-1 right-1"
                size="large"
                @click="updateMode(ModeEnum.forgotPassword)"
              >
                {{ t('login.forgotPassword') }}
              </el-button>
            </el-row>
            <goat-form-field-check-box
              api-field-name="rememberMe"
              :label-check-box="t('login.rememberMe')"
              :label="false"
              class="text-lg font-light ml-0.5 goat-large"
            />
            <goat-form-fields-group
              justify="center"
            >
              <el-button
                type="primary"
                class="text-white rounded-lg uppercase text-lg min-w-52"
                size="large"
                :loading="loading"
                :disabled="ban"
                @click="submit()"
              >
                {{ t('login.loginBtn') }}
              </el-button>
            </goat-form-fields-group>
            <goat-form-fields-group
              justify="center"
              class="mt-5 font-light text-[1.1rem]"
            >
              {{ t('login.noAccount') }}
              <el-button
                link
                size="large"
                class="text-inherit"
                @click="updateMode(ModeEnum.register)"
              >
                <span class="underline underline-offset-2 font-light ml-1 text-[1.1rem] text-inherit hover:text-gs-main-color">{{ t('login.signUpHere') }}</span>
              </el-button>
            </goat-form-fields-group>
            <goat-form-fields-group />
            <goat-form-fields-group>
              <div class="text-center font-light text-[1.1rem] mt-6 mb-4 w-full">
                {{ t('login.orLoginWith') }}
              </div>
              <div class="flex justify-center items-center w-full login">
                <goat-site-google-credentials :call-back="handleGoogleCredentialResponse" />
              </div>
            </goat-form-fields-group>
            <goat-form-fields-group>
              <div
                v-if="isEnabledFb"
                class="flex justify-center items-center w-full mt-8"
              >
                <goat-site-fb-credentials :call-back="handleFbCredentialResponse" />
              </div>
            </goat-form-fields-group>
          </template>
        </goat-form-component>
      </div>
    </div>
  </div>
</template>

<style scoped lang="postcss">
</style>
